import React, { useState, useEffect, createRef, useCallback, useMemo, memo, useRef } from 'react';
import TextareaAutosize from 'react-textarea-autosize';
import { Translate } from 'react-i18nify';
import styled from 'styled-components';
import backlight from '../libs/backlight';
import { isPlaying, TOOLBAR_OFFSET, TOOLBAR_HEIGHT } from '../utils';

const Style = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    height: 100%;
    width: 100%;
    /* Reclaim the previously-wasted 15% side padding so the video (and thus the
       subtitle line) has more room; the overlay font still scales to fit. The
       top keeps room for the floating action buttons (Tool.js) above the video. */
    padding: max(4%, ${TOOLBAR_OFFSET * 2 + TOOLBAR_HEIGHT}px) 5% 4%;

    .video {
        display: flex;
        align-items: center;
        justify-content: center;
        height: auto;
        width: auto;
        position: relative;

        video {
            position: relative;
            z-index: 10;
            outline: none;
            max-height: 500px;
            max-width: 100%;
            box-shadow: 0px 5px 25px 5px rgb(0 0 0 / 80%);
            background-color: #000;
            cursor: pointer;

            /* Keep a visible frame while loading/failed so the status overlay
               (and the eventual video) has somewhere to render. */
            &.is-busy {
                min-width: min(480px, 70%);
                min-height: 270px;
            }
        }

        .video-status {
            position: absolute;
            inset: 0;
            z-index: 25;
            display: flex;
            flex-direction: column;
            gap: 12px;
            align-items: center;
            justify-content: center;
            text-align: center;
            padding: 16px;
            color: #fff;
            font-size: 14px;
            background-color: rgb(0 0 0 / 55%);
            border-radius: 6px;
            pointer-events: none;

            &.is-error {
                pointer-events: all;
            }

            .video-status-btn {
                pointer-events: all;
                cursor: pointer;
                color: #fff;
                font-size: 13px;
                padding: 7px 16px;
                border-radius: 4px;
                border: 1px solid rgb(255 255 255 / 35%);
                background-color: rgb(255 255 255 / 12%);
            }

            .video-status-btn:hover {
                background-color: rgb(255 255 255 / 22%);
            }

            .video-status-spinner {
                width: 26px;
                height: 26px;
                border: 3px solid rgb(255 255 255 / 25%);
                border-top-color: #fff;
                border-radius: 50%;
                animation: video-spin 0.8s linear infinite;
            }
        }

        /* The host passes a light audio render as videoUrl and the original
           as fullVideoUrl. While only the audio is loaded, the black frame says
           so and offers the picture. Clicks pass through to the video
           (play/pause) everywhere except the button. */
        .audio-only {
            position: absolute;
            inset: 0;
            z-index: 30;
            display: flex;
            flex-direction: column;
            gap: 10px;
            align-items: center;
            justify-content: center;
            padding: 12px;
            text-align: center;
            color: #fff;
            pointer-events: none;

            .audio-only-label {
                font-size: 13px;
                opacity: 0.8;
            }
        }

        .load-full-video {
            pointer-events: all;
            display: flex;
            align-items: center;
            gap: 6px;
            padding: 6px 12px;
            color: #fff;
            font-size: 13px;
            border-radius: 4px;
            border: 1px solid rgb(255 255 255 / 35%);
            background-color: rgb(0 0 0 / 70%);
            cursor: pointer;

            &:hover,
            &:focus-visible {
                background-color: rgb(0 0 0 / 90%);
            }

            svg {
                flex-shrink: 0;
            }
        }

        @keyframes video-spin {
            to {
                transform: rotate(360deg);
            }
        }

        .subtitle {
            display: flex;
            flex-direction: column;
            justify-content: center;
            align-items: center;
            position: absolute;
            z-index: 20;
            left: 0;
            right: 0;
            bottom: 5%;
            width: 100%;
            padding: 0 20px;
            user-select: none;
            pointer-events: none;

            .operate {
                padding: 5px 15px;
                color: #fff;
                font-size: 13px;
                border-radius: 3px;
                margin-bottom: 5px;
                background-color: rgb(0 0 0 / 75%);
                border: 1px solid rgb(255 255 255 / 20%);
                cursor: pointer;
                pointer-events: all;
            }

            .textarea {
                box-sizing: content-box;
                /* Only the cue's own \n breaks — never soft-wrap (font shrinks to fit instead). */
                white-space: pre;
                overflow: hidden;
                outline: none;
                resize: none;
                text-align: center;
                line-height: 1.2;
                border: none;
                color: #fff;
                font-size: 20px;
                padding: 5px 10px;
                user-select: all;
                pointer-events: all;
                background-color: rgb(0 0 0 / 50%);
                border-radius: 10px;
                text-shadow: rgb(0 0 0) 1px 0px 1px, rgb(0 0 0) 0px 1px 1px, rgb(0 0 0) -1px 0px 1px, rgb(0 0 0) 0px -1px 1px;

                &.pause {
                    background-color: rgb(0 0 0 / 75%);
                }
            }
        }
    }
`;

const MAX_VIDEO_RETRIES = 3;
const VIDEO_WATCHDOG_MS = 9000;

// On-video subtitle font: starts at the default and only shrinks so the widest
// authored line fits the video. Horizontal space eaten by .subtitle (2×20px)
// and .textarea (2×10px) padding.
const SUB_FONT_PX = 20;
const SUB_H_PADDING_PX = 60;
// Caret room + canvas-vs-DOM subpixel rounding.
const SUB_SLACK_PX = 2;
const MEASURE_PX = 100;
const measureCtx = document.createElement('canvas').getContext('2d');

const VideoWrap = memo(
    ({ setPlayer, setCurrentTime, setPlaying }) => {
        const $video = useRef();
        const [videoUrl, setVideoUrl] = useState('');
        const [fullVideoUrl, setFullVideoUrl] = useState('');
        // Set once the browser has refused the full video's format.
        const [fullVideoFailed, setFullVideoFailed] = useState(false);
        // The light (audio) URL to fall back to if the full video can't play.
        const lightUrlRef = useRef('');
        // Playback position to restore after switching between the two files.
        const resumeAtRef = useRef(null);
        // Start playing once the full video is ready (set by the button click).
        const autoPlayRef = useRef(false);
        // Bumping the nonce changes the <video> src (cache-busted) which forces
        // the browser to re-fetch — our retry mechanism.
        const [reloadNonce, setReloadNonce] = useState(0);
        const [videoError, setVideoError] = useState(false);
        const [loadingVideo, setLoadingVideo] = useState(false);
        const retryCountRef = useRef(0);
        const watchdogRef = useRef(null);
        const lastBufferedRef = useRef(0);

        function getUrlParams(search) {
            const hashes = search.slice(search.indexOf('?') + 1).split('&');
            return hashes.reduce((params, hash) => {
              const split = hash.indexOf('=');

              if (split < 0) {
                return Object.assign(params, {[hash]: null});
              }

              const key = hash.slice(0, split);
              const val = hash.slice(split + 1);
              return Object.assign(params, {[key]: decodeURIComponent(val)});
            }, {});
          }

        // The original file is often large (tens of MB). A bare <video> with no
        // error/stall handling just goes blank on any transient network hiccup
        // and never recovers without a full page reload. The src below carries a
        // cache-bust suffix on retries so a poisoned/partial cache entry is bypassed.
        const effectiveSrc = useMemo(() => {
            if (!videoUrl) return '';
            if (!reloadNonce) return videoUrl;
            const sep = videoUrl.indexOf('?') >= 0 ? '&' : '?';
            return `${videoUrl}${sep}_retry=${reloadNonce}`;
        }, [videoUrl, reloadNonce]);

        const clearWatchdog = useCallback(() => {
            if (watchdogRef.current) {
                clearTimeout(watchdogRef.current);
                watchdogRef.current = null;
            }
        }, []);

        const triggerRetry = useCallback(() => {
            if (retryCountRef.current >= MAX_VIDEO_RETRIES) {
                clearWatchdog();
                setLoadingVideo(false);
                setVideoError(true);
                return;
            }
            retryCountRef.current += 1;
            setVideoError(false);
            setLoadingVideo(true);
            setReloadNonce((n) => n + 1);
        }, [clearWatchdog]);

        // Watchdog: if the media makes no buffering progress within the window,
        // treat it as stuck and retry. If it's merely slow (buffer advancing),
        // keep waiting instead of throwing away the bytes already downloaded.
        const armWatchdog = useCallback(() => {
            clearWatchdog();
            watchdogRef.current = setTimeout(function tick() {
                const el = $video.current;
                if (!el) return;
                if (el.readyState >= 3) {
                    clearWatchdog();
                    setLoadingVideo(false);
                    return;
                }
                const buffered = el.buffered && el.buffered.length ? el.buffered.end(el.buffered.length - 1) : 0;
                if (buffered > lastBufferedRef.current + 0.01) {
                    lastBufferedRef.current = buffered;
                    watchdogRef.current = setTimeout(tick, VIDEO_WATCHDOG_MS);
                    return;
                }
                triggerRetry();
            }, VIDEO_WATCHDOG_MS);
        }, [clearWatchdog, triggerRetry]);

        const onLoadStart = useCallback(() => {
            lastBufferedRef.current = 0;
            setLoadingVideo(true);
            armWatchdog();
        }, [armWatchdog]);

        const onCanPlay = useCallback(() => {
            clearWatchdog();
            retryCountRef.current = 0;
            setLoadingVideo(false);
            setVideoError(false);
        }, [clearWatchdog]);

        const onError = useCallback(() => {
            // The full video is optional. If the browser can't play it (code 4,
            // e.g. an MPEG-TS file saved as .mp4, or a missing file), retrying
            // just downloads it again: go back to the audio and say so.
            const el = $video.current;
            if (fullVideoUrl && videoUrl === fullVideoUrl && el && el.error && el.error.code === 4) {
                clearWatchdog();
                retryCountRef.current = 0;
                setReloadNonce(0);
                setVideoError(false);
                setFullVideoFailed(true);
                autoPlayRef.current = false;
                setVideoUrl(lightUrlRef.current);
                return;
            }
            // Otherwise waiting won't help either, retry now (within budget).
            triggerRetry();
        }, [triggerRetry, clearWatchdog, fullVideoUrl, videoUrl]);

        const onManualReload = useCallback((event) => {
            if (event) event.stopPropagation();
            retryCountRef.current = 0;
            setVideoError(false);
            setLoadingVideo(true);
            setReloadNonce((n) => n + 1);
        }, []);

        useEffect(() => {

            const params = getUrlParams(window.location.search);
            const videoUrlParam = params.videoUrl;


            const videoUrlToUse = videoUrlParam || localStorage.getItem('videoUrl');
            if (videoUrlToUse) {
              setVideoUrl(videoUrlToUse);
            }
            if (params.fullVideoUrl) {
              setFullVideoUrl(params.fullVideoUrl);
            }

            // Ostatak koda za postavljanje player-a
            setPlayer($video.current);
            (function loop() {
              window.requestAnimationFrame(() => {
                if ($video.current) {
                  setPlaying(isPlaying($video.current));
                  setCurrentTime($video.current.currentTime || 0);
                }
                loop();
              });
            })();
          }, [setPlayer, setCurrentTime, setPlaying, $video]);

        useEffect(() => clearWatchdog, [clearWatchdog]);

        const onLoadFullVideo = useCallback(
            (event) => {
                event.stopPropagation();
                if ($video.current) resumeAtRef.current = $video.current.currentTime || 0;
                lightUrlRef.current = videoUrl;
                autoPlayRef.current = true;
                retryCountRef.current = 0;
                setReloadNonce(0);
                setVideoError(false);
                setVideoUrl(fullVideoUrl);
            },
            [fullVideoUrl, videoUrl],
        );

        const onLoadedMetadata = useCallback(() => {
            const el = $video.current;
            if (!el) return;
            if (resumeAtRef.current) el.currentTime = resumeAtRef.current;
            resumeAtRef.current = null;
            // The click on Load full video counts as the user's permission to
            // play with sound. If a browser still blocks it, it just stays paused.
            if (autoPlayRef.current) {
                autoPlayRef.current = false;
                const playing = el.play();
                if (playing && playing.catch) playing.catch(() => {});
            }
        }, []);


        const onClick = useCallback(() => {
            if ($video.current) {
                if (isPlaying($video.current)) {
                    $video.current.pause();
                } else {
                    $video.current.play();
                }
            }
        }, [$video]);

        const busy = loadingVideo || videoError;

        const audioOnly = fullVideoUrl && videoUrl !== fullVideoUrl;

        // Shown in the audio-only frame, or in the error box if the audio fails.
        const loadFullButton =
            audioOnly && !fullVideoFailed ? (
                <button type="button" className="load-full-video" onClick={onLoadFullVideo}>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                        <rect x="2" y="5" width="14" height="14" rx="2" />
                        <path d="M16 10l6-3v10l-6-3z" />
                    </svg>
                    <Translate value="LOAD_FULL_VIDEO" />
                </button>
            ) : null;

        // The <video> must stay a DIRECT child of the .video container — the
        // backlight lib does `container.insertBefore(div, videoEl)`, which
        // throws if the video is nested in a wrapper. So use a Fragment and
        // anchor the status overlay to .video (which is position: relative).
        return (
            <>
                <video
                    onClick={onClick}
                    src={effectiveSrc}
                    ref={$video}
                    className={busy ? 'is-busy' : ''}
                    preload="metadata"
                    playsInline
                    onLoadStart={onLoadStart}
                    onLoadedMetadata={onLoadedMetadata}
                    onCanPlay={onCanPlay}
                    onError={onError}
                />
                {audioOnly && !busy ? (
                    <div className="audio-only">
                        <div className="audio-only-label">
                            <Translate value={fullVideoFailed ? 'FULL_VIDEO_UNSUPPORTED' : 'AUDIO_ONLY'} />
                        </div>
                        {loadFullButton}
                    </div>
                ) : null}
                {videoError ? (
                    <div className="video-status is-error" onClick={(e) => e.stopPropagation()}>
                        <div>
                            <Translate value="VIDEO_LOAD_FAILED" />
                        </div>
                        <div className="video-status-btn" onClick={onManualReload}>
                            <Translate value="VIDEO_RELOAD" />
                        </div>
                        {loadFullButton}
                    </div>
                ) : loadingVideo ? (
                    <div className="video-status">
                        <div className="video-status-spinner" />
                    </div>
                ) : null}
            </>
        );
    },
    () => true,
);

export default function Player(props) {
    const [currentSub, setCurrentSub] = useState(null);
    const [focusing, setFocusing] = useState(false);
    const [inputItemCursor, setInputItemCursor] = useState(0);
    const [videoWidth, setVideoWidth] = useState(0);
    const $player = createRef();
    const $subText = useRef();

    useEffect(() => {
        if ($player.current && props.player && !backlight.state) {
            backlight.state = true;
            backlight($player.current, props.player);
        }
    }, [$player, props.player]);

    // Track the rendered video width so the subtitle overlay font can scale to
    // it (like a real player). A fixed font soft-wraps long lines on narrow
    // players; scaling keeps each authored line on one line at any size.
    useEffect(() => {
        const el = props.player;
        if (!el || typeof ResizeObserver === 'undefined') return undefined;
        const update = () => setVideoWidth(el.clientWidth || 0);
        update();
        const ro = new ResizeObserver(update);
        ro.observe(el);
        return () => ro.disconnect();
    }, [props.player]);

    useMemo(() => {
        setCurrentSub(props.subtitle[props.currentIndex]);
    }, [props.subtitle, props.currentIndex]);

    const onChange = useCallback(
        (event) => {
            props.player.pause();
            props.updateSub(currentSub, { text: event.target.value });
            if (event.target.selectionStart) {
                setInputItemCursor(event.target.selectionStart);
            }
        },
        [props, currentSub],
    );

    const onClick = useCallback(
        (event) => {
            props.player.pause();
            if (event.target.selectionStart) {
                setInputItemCursor(event.target.selectionStart);
            }
        },
        [props],
    );

    const onFocus = useCallback((event) => {
        setFocusing(true);
        if (event.target.selectionStart) {
            setInputItemCursor(event.target.selectionStart);
        }
    }, []);

    const onBlur = useCallback(() => {
        setTimeout(() => setFocusing(false), 500);
    }, []);

    const onSplit = useCallback(() => {
        props.splitSub(currentSub, inputItemCursor);
    }, [props, currentSub, inputItemCursor]);

    // Measure the widest authored line in the textarea's real font (not `ch`,
    // which is the width of "0" and undercounts wide glyphs), then shrink the
    // font from the default until that line fits the video width.
    const subText = currentSub ? currentSub.text || '' : '';
    const { subFontPx, subWidthPx } = useMemo(() => {
        const family = getComputedStyle($subText.current || document.body).fontFamily;
        measureCtx.font = `${MEASURE_PX}px ${family}`;
        const widest = subText.split('\n').reduce((m, l) => Math.max(m, measureCtx.measureText(l).width), 0);
        // -1: the Math.ceil on the width below.
        const avail = videoWidth - SUB_H_PADDING_PX - SUB_SLACK_PX - 1;
        const fontPx =
            widest && avail > 0
                ? Math.min(SUB_FONT_PX, Math.floor(((avail * MEASURE_PX) / widest) * 10) / 10)
                : SUB_FONT_PX;
        return { subFontPx: fontPx, subWidthPx: Math.ceil((widest * fontPx) / MEASURE_PX) + SUB_SLACK_PX };
    }, [subText, videoWidth]);

    return (
        <Style className="player">
            <div className="video" ref={$player}>
                <VideoWrap {...props} />
                {props.player && currentSub ? (
                    <div className="subtitle">
                        {focusing ? (
                            <div className="operate" onClick={onSplit}>
                                <Translate value="SPLIT" />
                            </div>
                        ) : null}
                        <TextareaAutosize
                            ref={$subText}
                            wrap="off"
                            className={`textarea ${!props.playing ? 'pause' : ''}`}
                            value={currentSub.text}
                            // Exactly the file's lines: width = measured widest line,
                            // font already shrunk so that line fits the video.
                            style={{
                                width: `${subWidthPx}px`,
                                fontSize: `${subFontPx}px`,
                            }}
                            onChange={onChange}
                            onClick={onClick}
                            onFocus={onFocus}
                            onBlur={onBlur}
                            onKeyDown={onFocus}
                            spellCheck={false}
                        />
                    </div>
                ) : null}
            </div>
        </Style>
    );
}
